"use client";

import React from "react";
import { MapPin, Phone, Mail, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-primary/5 border border-secondary/20 rounded-lg animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="h-8 w-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-serif text-secondary mb-2">Message Sent</h3>
        <p className="text-primary-foreground/80">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-serif">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary" />
              </FormControl>
              <FormDescription className="text-primary-foreground/40 text-xs">First and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-serif">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@example.com" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-serif">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary" />
              </FormControl>
              <FormDescription className="text-primary-foreground/40 text-xs"><br /></FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-serif">Budget / Price Range</FormLabel>
              <FormControl>
                <Input placeholder="$300,000 – $500,000" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary" />
              </FormControl>
              <FormDescription className="text-primary-foreground/40 text-xs">Provide a range if you have one.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-serif block mb-3">I’m Interested In</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {["Buying a House", "Selling a Property", "Investing", "General Question"].map((option, i) => <label key={option} data-index={i} className={`
                      flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all
                      ${field.value === option ? "border-secondary bg-secondary/10 text-secondary" : "border-primary-foreground/20 text-primary-foreground/70 hover:border-secondary/50 hover:text-secondary/80"}
                    `}>
                    <input type="radio" name={field.name} value={option} checked={field.value === option} onChange={field.onChange} className="accent-secondary h-4 w-4" />
                    <span className="text-sm font-medium">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      maxLength: {
        value: 1000,
        message: "Message too long"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-serif">Message</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us more about what you’re looking for…" className="flex min-h-[120px] w-full rounded-md border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-2 text-sm text-primary-foreground shadow-xs placeholder:text-primary-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-secondary disabled:cursor-not-allowed disabled:opacity-50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_nar" rules={{
      required: "You must acknowledge this policy."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-primary-foreground/10 p-4 bg-primary-foreground/5">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 h-4 w-4 accent-secondary rounded border-primary-foreground/30" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary-foreground/90 font-normal">
                I acknowledge that this submission complies with NAR advertising policies.
              </FormLabel>
              <FormDescription className="text-primary-foreground/50 text-xs">
                Required for compliance.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs px-4 py-2 has-[>svg]:px-3 w-full min-w-[200px] bg-secondary font-serif text-lg h-12 !text-[hsl(0,0%,0%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 md:w-auto hover:bg-secondary/90">
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
    </Form>;
}
export default function RealEstateHub() {
  return <section id="realestatehub" className="text-primary-foreground w-full overflow-hidden !bg-[hsl(0,0%,0%)]">
      <div className="container mx-auto px-4 py-[120px] flex flex-col gap-[120px]">
        
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto space-y-8">
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-tight text-primary-foreground">Find Your Home&nbsp;</h1>
          <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl font-light">
            Discover properties that tell a story. From historic colonials in Warwick to modern estates in Goshen.
          </p>
          
          <div className="w-full max-w-2xl relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-secondary/20 to-secondary/0 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
            <div className="relative flex items-center bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 rounded-lg p-2 shadow-2xl">
              <MapPin className="ml-3 text-secondary h-5 w-5 shrink-0" />
              <Input className="border-0 bg-transparent text-lg text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-0 focus-visible:ring-offset-0 h-12" placeholder="Search by city, zip, or address..." />
              <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-serif px-8">
                Search
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Badge variant="outline" className="border-secondary/50 text-secondary">Market Insights</Badge>
            <h2 className="text-3xl md:text-5xl font-serif text-primary-foreground">
              Orange County <br />
              <span className="text-secondary">Real Estate Trends</span>
            </h2>
            <p className="text-primary-foreground/70 text-lg leading-relaxed">
              Stay ahead of the curve with our real-time market analysis. Whether you're buying or selling, understanding the local data is key to making informed decisions in today's dynamic landscape.
            </p>
            <div className="grid grid-cols-2 gap-6 pt-4">
              <div className="p-4 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                <div className="text-3xl font-serif text-secondary mb-1">+4.2%</div>
                <div className="text-sm text-primary-foreground/60">Avg. House Value (YoY)</div>
              </div>
              <div className="p-4 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                <div className="text-3xl font-serif text-secondary mb-1">28</div>
                <div className="text-sm text-primary-foreground/60">Avg. Days on Market</div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          <div className="lg:col-span-5 space-y-8">
            <h2 className="text-4xl md:text-5xl font-serif text-primary-foreground leading-tight">
              Let's Start Your <br />
              <span className="text-secondary">Journey House</span>
            </h2>
            <p className="text-lg text-primary-foreground/70">
              Whether you're ready to buy, sell, or just have questions about the Orange County market, we're here to help.
            </p>
            
            <div className="space-y-6 pt-8">
              <div className="flex items-start gap-4">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <MapPin className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-primary-foreground">Visit Us</h4>
                  <p className="text-primary-foreground/60">HomeSmart Homes And Estates
                  <div>LIC: 10311205625
                  </div><div>1 Hawkins Drive Montgomery, NY 12549</div></p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <Phone className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-primary-foreground">Call me</h4>
                  <p className="text-primary-foreground/60">(845) 787-6872</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <Mail className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-primary-foreground">Email me</h4>
                  <p className="text-primary-foreground/60">findahomewithchris@gmail.com</p>
                </div>
              </div>
            </div>
          </div>

          
        </div>

        

      </div>
    </section>;
}